import { useCallback, useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { tblSpecLoadStorage, tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { tableFiltersVM, tableStatsVM, tableViewVM } from '../../../../core/table/table_view_models';
import { GROUPS_LIST_STORAGE_KEY, GROUPS_LIST_VIEW_KEY } from '../../groups_const';
import {
    tableStateAtom,
    tableViewAtom,
    groupsDataAtom,
    tableStatsAtom,
    tableFiltersAtom,
    setAtomGroupListTableViewState,
} from '../groups_list_atoms';
import { groupsListColumnSpec, groupsListDefaultView } from './GroupsListTableSpec';
import groupsListApiToVM from '../../data/groupsListApiToVM';
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { tableDataVM, tableStateVM } from '../../../../core/table/table_view_models';
import { GroupsDataItem } from '../groups_list_types';
import { ListTableInitHookReturn } from '../../../../core/table/table.types';
import apiSvc from '../../../../core/services/apiSvc';
import { GET_LIST } from '../../data/groupsRepo';

export const useGroupsListTableInit = (): ListTableInitHookReturn => {
    const [{ init }, setState] = useImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const setList = useSetImmerAtom(groupsDataAtom);
    const setStats = useSetImmerAtom(tableStatsAtom);
    const setFilter = useSetImmerAtom(tableFiltersAtom);

    const getListAsync = useCallback(async () => {
        try {
            const r = await apiSvc.getEntityList<GroupsDataItem>(GET_LIST, {});
            setList(draft => {
                draft.dataItems = groupsListApiToVM(r); // convert incoming API response into strongly typed Table items
                draft.items = [];
            });
        } catch (err: any) {
            // err msg handled upstream
            setList(() => tableDataVM<GroupsDataItem>());
        } finally {
            setState(draft => {
                draft.waiting = false;
                draft.init = false;
                return draft;
            });
        }
    }, [setList, setState]);

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init && !draft.waiting) {
                // load custom views from storage
                const colSpec = groupsListColumnSpec();
                const defaultView = groupsListDefaultView();
                const viewStorage = tblSpecLoadStorage(GROUPS_LIST_VIEW_KEY, colSpec, defaultView);
                draft.views = viewStorage.views;
                draft.currentViewId = viewStorage.current;

                // set current view
                setView(() => tblSpecLoadView(viewStorage.current, viewStorage.views, colSpec) || tableViewVM());

                // viewstate filters and paging
                const viewstate = viewStateRepo.getTable(GROUPS_LIST_STORAGE_KEY, Array.from(colSpec.keys()));
                if (!!viewstate) {
                    setView(draft => {
                        draft.sort = viewstate.sort;
                    });
                    setFilter(() => viewstate.flt);
                    setStats(draft => {
                        draft.paging = viewstate.paging;
                    });
                }

                getListAsync();

                draft.waiting = true;
                return draft;
            } else {
                return draft;
            }
        });

        // destroy
        return (): void => {
            setAtomGroupListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<GroupsDataItem>());
        };
    }, [setState, setView, getListAsync, setList, setFilter, setStats]);

    return { init };
};
